/**
 * useGenerationStream - Custom hook for SSE-based generation progress.
 *
 * Wraps EventSource API to receive real-time generation progress events.
 * Falls back to polling if SSE connection fails.
 *
 * SSE Endpoint: /api/v1/internal/assessments/:id/generation-stream
 *
 * Events received:
 * - `connected`: Initial connection with jobId and status
 * - `progress`: GenerationProgressEvent (phase, agent, message, progress %)
 * - `close`: Job completed, stream ending
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../lib/api';
import type { PackageSummary } from '../lib/types';

// Mirror the backend event types
export type AgentRole =
  | 'orchestrator'
  | 'competency-extractor'
  | 'research-analyst'
  | 'scenario-designer'
  | 'persona-architect'
  | 'rubric-engineer'
  | 'quality-validator'
  | 'synthesis-engine';

export type GenerationPhase =
  | 'competency_extraction'
  | 'parallel_research'
  | 'synthesis'
  | 'parallel_content'
  | 'quality_validation'
  | 'completed'
  | 'failed';

export interface GenerationProgressEvent {
  eventId: string;
  phase: GenerationPhase;
  agent: AgentRole;
  message: string;
  progress: number;
  timestamp: string;
  detail?: string;
  agentComplete: boolean;
  error?: string;
}

export interface AgentState {
  role: AgentRole;
  displayName: string;
  status: 'idle' | 'working' | 'complete' | 'error';
  lastMessage: string;
  detail?: string;
}

const AGENT_DISPLAY_NAMES: Record<AgentRole, string> = {
  'orchestrator': 'Orchestrator',
  'competency-extractor': 'Competency Analyst',
  'research-analyst': 'Domain Research',
  'scenario-designer': 'Scenario Designer',
  'persona-architect': 'Persona Architect',
  'rubric-engineer': 'Rubric Engineer',
  'quality-validator': 'Quality Validator',
  'synthesis-engine': 'Synthesis Engine',
};

interface UseGenerationStreamOptions {
  assessmentId: string;
  enabled: boolean;
}

interface UseGenerationStreamResult {
  /** All progress events received */
  events: GenerationProgressEvent[];
  /** Current generation phase */
  currentPhase: GenerationPhase | null;
  /** State of each agent */
  agents: Map<AgentRole, AgentState>;
  /** Overall progress 0-100 */
  progress: number;
  /** Whether generation is complete */
  isComplete: boolean;
  /** Whether generation failed */
  isFailed: boolean;
  /** Error message if failed */
  errorMessage: string | null;
  /** Whether SSE is connected (false = using polling fallback) */
  isStreaming: boolean;
  /** Package summary from polling fallback */
  packageSummary: PackageSummary | null;
}

const API_PREFIX = '/api/v1/internal';

/** Polling interval for fallback (ms) */
const POLL_INTERVAL = 2000;

/** Max SSE reconnect attempts before falling back to polling */
const MAX_SSE_RETRIES = 3;

export function useGenerationStream({
  assessmentId,
  enabled,
}: UseGenerationStreamOptions): UseGenerationStreamResult {
  const [events, setEvents] = useState<GenerationProgressEvent[]>([]);
  const [currentPhase, setCurrentPhase] = useState<GenerationPhase | null>(null);
  const [agents, setAgents] = useState<Map<AgentRole, AgentState>>(new Map());
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [packageSummary, setPackageSummary] = useState<PackageSummary | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sseRetryCount = useRef(0);
  const usingPollingFallback = useRef(false);

  // Update agent state from a progress event
  const handleProgressEvent = useCallback((event: GenerationProgressEvent) => {
    setEvents((prev) => [...prev, event]);
    setCurrentPhase(event.phase);
    setProgress(event.progress);

    if (event.phase === 'completed') {
      setIsComplete(true);
      return;
    }

    if (event.phase === 'failed') {
      setIsFailed(true);
      setErrorMessage(event.error ?? event.message);
      return;
    }

    // Update agent state
    setAgents((prev) => {
      const next = new Map(prev);
      next.set(event.agent, {
        role: event.agent,
        displayName: AGENT_DISPLAY_NAMES[event.agent] ?? event.agent,
        status: event.agentComplete ? 'complete' : event.error ? 'error' : 'working',
        lastMessage: event.message,
        detail: event.detail,
      });
      return next;
    });
  }, []);

  // SSE connection
  const connectSSE = useCallback(() => {
    if (!enabled || !assessmentId) return;

    // Build SSE URL with auth token
    // EventSource doesn't support custom headers, so we need to use a query param
    // or rely on cookies. For now, use the base URL directly since the Vite proxy
    // will forward cookies/auth. If that doesn't work, fall back to polling.
    const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
    const url = `${baseUrl}${API_PREFIX}/assessments/${assessmentId}/generation-stream`;

    try {
      const es = new EventSource(url);
      eventSourceRef.current = es;

      es.addEventListener('connected', (e) => {
        setIsStreaming(true);
        sseRetryCount.current = 0;
        try {
          const data = JSON.parse((e as MessageEvent).data) as { jobId: string; status: string };
          // If job is already completed when we connect, mark it
          if (data.status === 'completed') {
            setIsComplete(true);
          }
        } catch {
          // Ignore parse errors for connected event
        }
      });

      es.addEventListener('progress', (e) => {
        try {
          const event = JSON.parse((e as MessageEvent).data) as GenerationProgressEvent;
          handleProgressEvent(event);
        } catch {
          // Ignore malformed events
        }
      });

      es.addEventListener('close', () => {
        setIsComplete(true);
        es.close();
        eventSourceRef.current = null;
        setIsStreaming(false);
      });

      es.onerror = () => {
        es.close();
        eventSourceRef.current = null;
        setIsStreaming(false);

        sseRetryCount.current += 1;
        if (sseRetryCount.current >= MAX_SSE_RETRIES) {
          // Fall back to polling
          usingPollingFallback.current = true;
          startPolling();
        } else {
          // Retry SSE after a delay
          setTimeout(connectSSE, 1000 * sseRetryCount.current);
        }
      };
    } catch {
      // SSE not supported or URL issue - fall back to polling
      usingPollingFallback.current = true;
      startPolling();
    }
  }, [enabled, assessmentId, handleProgressEvent]);

  // Polling fallback
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return; // Already polling

    const poll = async () => {
      try {
        const result = await apiClient.getGenerationStatus(assessmentId);
        if (!result.response.data) return;

        const status = result.response.data;

        if (status.status === 'completed') {
          setIsComplete(true);
          setProgress(100);
          setCurrentPhase('completed');
          if (status.package) {
            setPackageSummary(status.package);
          }
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          return;
        }

        if (status.status === 'failed') {
          setIsFailed(true);
          setErrorMessage(status.errorMessage ?? 'Generation failed');
          setCurrentPhase('failed');
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          return;
        }

        // Map polling status to approximate phase/progress
        if (status.status) {
          setCurrentPhase(mapPollingStatusToPhase(status.status));
          setProgress(mapPollingStatusToProgress(status.status));
        }

        if (status.progressMessage) {
          // Synthesize a progress event from polling data
          setEvents((prev) => {
            const lastMsg = prev[prev.length - 1]?.message;
            if (lastMsg === status.progressMessage) return prev; // Dedupe
            return [
              ...prev,
              {
                eventId: `poll-${Date.now()}`,
                phase: mapPollingStatusToPhase(status.status ?? 'pending'),
                agent: 'orchestrator' as AgentRole,
                message: status.progressMessage!,
                progress: mapPollingStatusToProgress(status.status ?? 'pending'),
                timestamp: new Date().toISOString(),
                agentComplete: false,
              },
            ];
          });
        }
      } catch {
        // Polling error - continue trying
      }
    };

    poll(); // Initial poll
    pollIntervalRef.current = setInterval(poll, POLL_INTERVAL);
  }, [assessmentId]);

  // Start connection when enabled
  useEffect(() => {
    if (!enabled) return;

    // Try SSE first
    connectSSE();

    return () => {
      // Cleanup
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [enabled, connectSSE]);

  return {
    events,
    currentPhase,
    agents,
    progress,
    isComplete,
    isFailed,
    errorMessage,
    isStreaming,
    packageSummary,
  };
}

// Helper: Map polling status string to GenerationPhase
// Backend statuses map to 5 frontend visual phases:
//   pending        → competency_extraction (Analyze)
//   analyzing      → parallel_research     (Research)
//   gen_competency → synthesis             (Synthesize)
//   gen_scenario   → parallel_content      (Create)
//   gen_persona    → parallel_content      (Create)
//   gen_rubric     → quality_validation    (Polish)
function mapPollingStatusToPhase(status: string): GenerationPhase {
  switch (status) {
    case 'pending':
      return 'competency_extraction';
    case 'analyzing':
      return 'parallel_research';
    case 'generating_competencies':
      return 'synthesis';
    case 'generating_scenario':
    case 'generating_persona':
      return 'parallel_content';
    case 'generating_rubric':
      return 'quality_validation';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    default:
      return 'competency_extraction';
  }
}

// Helper: Map polling status to approximate progress percentage
function mapPollingStatusToProgress(status: string): number {
  switch (status) {
    case 'pending':
      return 8;
    case 'analyzing':
      return 22;
    case 'generating_competencies':
      return 40;
    case 'generating_scenario':
      return 55;
    case 'generating_persona':
      return 70;
    case 'generating_rubric':
      return 85;
    case 'completed':
      return 100;
    case 'failed':
      return 0;
    default:
      return 0;
  }
}
